import { AuthService } from '../services/AuthService.js';

const OVERLAY_ID = 'roe-password-reset-overlay';

/**
 * Mounts a blocking DOM overlay on top of the Phaser canvas when the page
 * URL contains `?reset=<token>`. Two password fields, Submit, inline error.
 * On success: scrubs the token from the URL (`history.replaceState` to `/`),
 * destroys the overlay, and asks LoginScene to show a green success banner.
 *
 * Mirrors the pattern in `version/updateRequiredOverlay.ts` exactly: fixed
 * position, z-index 10000, idempotent, bronze-gold palette.
 *
 * @param token The raw reset token (43-char URL-safe) from the reset link.
 * @param onSuccess Optional callback called after the token is successfully
 *   consumed — typically used by main.ts to `game.scene.start('LoginScene',
 *   { resetBanner: 'success' })`. The overlay itself scrubs the URL before
 *   firing this so the callback sees a clean `/`.
 */
export function mountResetOverlay(token: string, onSuccess?: () => void): void {
  if (document.getElementById(OVERLAY_ID)) return;

  const overlay = document.createElement('div');
  overlay.id = OVERLAY_ID;
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  Object.assign(overlay.style, {
    position: 'fixed',
    top: '0px',
    left: '0px',
    width: '100%',
    height: '100%',
    zIndex: '10000',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(13, 10, 14, 0.92)',
    fontFamily: 'Georgia, serif',
  } as CSSStyleDeclaration);

  const card = document.createElement('div');
  Object.assign(card.style, {
    width: '420px',
    padding: '28px 24px',
    background: '#0d0a0e',
    border: '1px solid #5a3600',
    borderRadius: '6px',
    boxShadow: '0 0 24px rgba(200, 151, 58, 0.2)',
    color: '#e0e0e0',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  } as CSSStyleDeclaration);

  const title = document.createElement('h1');
  title.textContent = 'Reset password';
  Object.assign(title.style, {
    margin: '0 0 8px 0',
    fontSize: '22px',
    color: '#ffd700',
    textAlign: 'center',
  } as CSSStyleDeclaration);

  const subtitle = document.createElement('p');
  subtitle.textContent = 'Enter a new password for your account.';
  Object.assign(subtitle.style, {
    margin: '0 0 8px 0',
    fontSize: '13px',
    color: '#a07840',
    textAlign: 'center',
  } as CSSStyleDeclaration);

  const inputStyle = {
    padding: '10px 12px',
    fontSize: '14px',
    fontFamily: 'inherit',
    background: '#1a0f06',
    border: '1px solid #5a3600',
    color: '#e0e0e0',
    borderRadius: '3px',
    outline: 'none',
  } as Partial<CSSStyleDeclaration>;

  const pass1 = document.createElement('input');
  pass1.type = 'password';
  pass1.placeholder = 'New password (6+ chars)';
  pass1.autocomplete = 'new-password';
  Object.assign(pass1.style, inputStyle);

  const pass2 = document.createElement('input');
  pass2.type = 'password';
  pass2.placeholder = 'Confirm password';
  pass2.autocomplete = 'new-password';
  Object.assign(pass2.style, inputStyle);

  const errorEl = document.createElement('div');
  Object.assign(errorEl.style, {
    fontSize: '12px',
    color: '#ef4444',
    minHeight: '16px',
    textAlign: 'center',
  } as CSSStyleDeclaration);

  const submit = document.createElement('button');
  submit.type = 'button';
  submit.textContent = 'Update password';
  Object.assign(submit.style, {
    marginTop: '4px',
    padding: '10px 16px',
    fontFamily: 'inherit',
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#0d0a0e',
    background: '#c8973a',
    border: '1px solid #5a3600',
    borderRadius: '3px',
    cursor: 'pointer',
  } as CSSStyleDeclaration);

  const setError = (msg: string): void => { errorEl.textContent = msg; };
  const canSubmit = (): boolean =>
    pass1.value.length >= 6 && pass1.value === pass2.value;

  const updateSubmitState = (): void => {
    submit.disabled = !canSubmit();
    submit.style.opacity = canSubmit() ? '1' : '0.5';
    submit.style.cursor = canSubmit() ? 'pointer' : 'not-allowed';
  };
  pass1.addEventListener('input', updateSubmitState);
  pass2.addEventListener('input', updateSubmitState);
  updateSubmitState();

  submit.addEventListener('click', async () => {
    setError('');
    if (pass1.value.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (pass1.value !== pass2.value) { setError("Passwords don't match."); return; }

    submit.disabled = true;
    try {
      await AuthService.getInstance().resetPassword(token, pass1.value);
      // Scrub the token from the URL so a browser back/reload doesn't retry.
      window.history.replaceState({}, '', '/');
      overlay.remove();
      onSuccess?.();
    } catch (err) {
      setError((err as Error).message ?? 'Reset failed — please request a new link.');
      submit.disabled = false;
    }
  });

  card.append(title, subtitle, pass1, pass2, errorEl, submit);
  overlay.appendChild(card);
  document.body.appendChild(overlay);
}
